import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, errorMessage } from "../api/client";

export type Profile = {
  id: string; email: string; first_name: string; last_name: string; timezone: string; locale: string; avatar_url: string;
  phone: string; default_site: string | null; notification_preferences: Record<string, unknown>;
  ui_preferences: Record<string, unknown>; date_joined: string; last_login: string | null;
};

export function useProfile() {
  return useQuery({
    queryKey: ["profile"],
    queryFn: async () => {
      const { data, error } = await api.GET("/api/v1/accounts/me/profile/");
      if (error) throw new Error(errorMessage(error));
      return data as unknown as Profile;
    },
  });
}

export function useUpdateProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (patch: Partial<Profile>) => {
      const { data, error } = await api.PATCH("/api/v1/accounts/me/profile/", { body: patch as never });
      if (error) throw error;
      return data as unknown as Profile;
    },
    onSuccess: (data) => { qc.setQueryData(["profile"], data); void qc.invalidateQueries({ queryKey: ["me"] }); },
  });
}

export type MfaStatus = { enabled: boolean; required: boolean; confirmed_at: string | null; recovery_codes_remaining: number };
export type Session = { id: string; ip_address: string | null; user_agent: string; created_at: string; last_seen_at: string; is_current: boolean };

export function useMfaStatus() {
  return useQuery({ queryKey: ["mfa"], queryFn: async () => (await api.GET("/api/v1/accounts/auth/mfa/")).data as unknown as MfaStatus });
}

export function useSessions() {
  return useQuery({ queryKey: ["sessions"], queryFn: async () => ((await api.GET("/api/v1/accounts/auth/sessions/")).data ?? []) as unknown as Session[] });
}

export function useAuthEvents() {
  return useQuery({ queryKey: ["auth-events"], queryFn: async () => ((await api.GET("/api/v1/accounts/auth/events/")).data ?? []) as unknown as { kind: string; ip_address: string | null; user_agent: string; detail: string; created_at: string }[] });
}
